/**
 * WebSocket 客户端
 */
import type {
  WSMessage,
  WSAuthMessage,
  WSMessageNotification,
  WSTypingMessage,
  WSTaskStatusMessage,
  WSHUDStatusMessage,
} from '../api/types.js';
import { RECONNECT_DELAY } from '../config.js';

/**
 * WebSocket 事件监听器
 */
export interface WSEventListeners {
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
  onAuthSuccess?: () => void;
  onAuthFailed?: (error: string) => void;
  onMessage?: (sender: 'user' | 'assistant', content: string, timestamp: string, senderName?: string, model?: string) => void;
  onTyping?: (isTyping: boolean) => void;
  onTaskStatus?: (status: string, message?: string, progress?: number) => void;
  onHUDStatus?: (hudStatus: WSHUDStatusMessage) => void;
}

/**
 * WebSocket 客户端类
 */
export class WSClient {
  private url: string;
  private token: string | null = null;
  private ws: WebSocket | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private isManualClose = false;
  private listeners: WSEventListeners = {};

  constructor(url: string) {
    this.url = url;

    // 监听页面可见性变化（处理标签休眠后恢复）
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // 标签恢复可见时，检查连接状态
        // 只有在明确断开且没有重连定时器时才重连
        if (!this.isConnected() && !this.reconnectTimeout && !this.isManualClose) {
          console.log('Page visible, WebSocket disconnected, reconnecting...');
          this.connect();
        }
      }
    });
  }

  /**
   * 设置事件监听器
   */
  setListeners(listeners: WSEventListeners): void {
    this.listeners = { ...this.listeners, ...listeners };
  }

  /**
   * 设置认证 Token
   */
  setToken(token: string): void {
    this.token = token;
    // 如果已连接，重新认证
    if (this.isConnected()) {
      this.authenticate();
    }
  }

  /**
   * 连接 WebSocket
   */
  connect(): void {
    // 如果已经连接，不重复连接
    if (this.isConnected()) {
      console.log('WebSocket already connected, skipping');
      return;
    }

    // 清理现有连接
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.isManualClose = false;

    try {
      this.ws = new WebSocket(this.url);
      this.setupEventHandlers();
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * 设置事件处理器
   */
  private setupEventHandlers(): void {
    if (!this.ws) return;

    // 添加心跳保活定时器
    let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      // 连接成功后进行认证
      this.authenticate();
      this.listeners.onOpen?.();

      // 启动心跳保活：每30秒发送一次ping
      heartbeatInterval = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    this.ws.onmessage = (event) => {
      // 更新最后活动时间
      try {
        const data = JSON.parse(event.data) as WSMessage;
        this.handleMessage(data);
      } catch (error) {
        // 忽略 ping/pong 等非 JSON 消息
        if (event.data !== 'pong' && event.data !== 'ping') {
          console.error('WebSocket message parse error:', error);
        }
      }
    };

    this.ws.onclose = () => {
      console.log('WebSocket disconnected');
      this.ws = null;
      this.listeners.onClose?.();

      // 清除心跳定时器
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
      }

      // 如果不是手动关闭，尝试重连
      if (!this.isManualClose) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.listeners.onError?.(error);
    };
  }

  /**
   * 处理收到的消息
   */
  private handleMessage(data: WSMessage): void {
    switch (data.type) {
      case 'auth_success':
        console.log('WebSocket authenticated');
        this.listeners.onAuthSuccess?.();
        break;

      case 'auth_failed':
        console.error('WebSocket authentication failed:', data);
        this.listeners.onAuthFailed?.(data.error || '认证失败');
        break;

      case 'message': {
        const msg = data as WSMessageNotification;
        this.listeners.onMessage?.(msg.sender, msg.content, msg.timestamp, msg.senderName, msg.model);
        break;
      }

      case 'typing': {
        const typing = data as WSTypingMessage;
        this.listeners.onTyping?.(typing.isTyping);
        break;
      }

      case 'task_status': {
        const taskStatus = data as WSTaskStatusMessage;
        this.listeners.onTaskStatus?.(taskStatus.status, taskStatus.message, taskStatus.progress);
        break;
      }

      case 'hud_status': {
        const hudStatus = data as WSHUDStatusMessage;
        this.listeners.onHUDStatus?.(hudStatus);
        break;
      }

      case 'error':
        console.error('WebSocket error from server:', data);
        break;

      default:
        console.warn('Unknown WebSocket message type:', data);
    }
  }

  /**
   * 认证
   */
  private authenticate(): void {
    if (!this.token) {
      console.warn('No token available for WebSocket authentication');
      return;
    }

    if (!this.isConnected()) {
      console.warn('WebSocket not connected, cannot authenticate');
      return;
    }

    const authMessage: WSAuthMessage = {
      type: 'auth',
      token: this.token,
    };

    this.send(authMessage);
  }

  /**
   * 发送消息
   */
  send(data: WSMessage): void {
    if (!this.isConnected()) {
      console.warn('WebSocket not connected, cannot send message');
      return;
    }

    try {
      this.ws!.send(JSON.stringify(data));
    } catch (error) {
      console.error('WebSocket send error:', error);
    }
  }

  /**
   * 发送聊天消息
   */
  sendChat(content: string): void {
    this.send({
      type: 'chat',
      content,
    });
  }

  /**
   * 发送打字指示器
   */
  sendTyping(isTyping: boolean): void {
    this.send({
      type: 'typing',
      isTyping,
    });
  }

  /**
   * 安排重连
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectTimeout = setTimeout(() => {
      console.log('Attempting to reconnect WebSocket...');
      this.connect();
    }, RECONNECT_DELAY);
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * 断开连接
   */
  disconnect(): void {
    this.isManualClose = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

/**
 * 创建单例实例
 */
let wsClient: WSClient | null = null;

export function createWSClient(url: string): WSClient {
  if (!wsClient) {
    wsClient = new WSClient(url);
  }
  return wsClient;
}

export function getWSClient(): WSClient | null {
  return wsClient;
}
